/**
 * Telemetry API client for the survey player.
 * Handles event batching, offline resilience, and randomization map persistence.
 */

import { apiClient } from './client';

// ============================================================================
// TYPES
// ============================================================================

/**
 * Session event types matching backend enum
 */
export enum SessionEventType {
  // Session lifecycle
  SESSION_STARTED = 'session.started',
  SESSION_COMPLETED = 'session.completed',
  SESSION_TERMINATED = 'session.terminated',
  SESSION_DROPPED = 'session.dropped',

  // Page events
  PAGE_RENDERED = 'page.rendered',
  PAGE_SUBMITTED = 'page.submitted',

  // Question events
  QUESTION_SHOWN = 'question.shown',
  QUESTION_HIDDEN = 'question.hidden',
  QUESTION_FOCUSED = 'question.focused',

  // Answer events
  ANSWER_CHANGED = 'answer.changed',
  ANSWER_CLEARED = 'answer.cleared',

  // Validation events
  VALIDATION_FAILED = 'validation.failed',
  VALIDATION_PASSED = 'validation.passed',

  // Logic events
  LOGIC_FIRED = 'logic.fired',
  LOGIC_SKIP = 'logic.skip',
  LOGIC_DISPLAY = 'logic.display',
  LOGIC_TERMINATE = 'logic.terminate',

  // Navigation events
  NAVIGATION = 'navigation',
  NAVIGATION_BACK = 'navigation.back',
  NAVIGATION_JUMP = 'navigation.jump',

  // Randomization events
  RANDOMIZATION_APPLIED = 'randomization.applied',

  // Quality signals
  QUALITY_SPEEDING = 'quality.speeding',
  QUALITY_STRAIGHTLINING = 'quality.straightlining',
  QUALITY_TAB_SWITCH = 'quality.tab_switch',
}

export interface SessionEventPayload {
  questionCode?: string;
  pageNumber?: number;
  oldValue?: unknown;
  newValue?: unknown;
  valueType?: string;
  ruleId?: string;
  ruleType?: string;
  conditionResult?: boolean;
  action?: string;
  targetQuestionCode?: string;
  fromQuestion?: string;
  toQuestion?: string;
  reason?: string;
  validationType?: string;
  message?: string;
  blockId?: string;
  mapHash?: string;
  questionOrder?: string[];
  optionOrder?: Record<string, string[]>;
  timeSpent?: number;
  expectedMinTime?: number;
  patternScore?: number;
  questionCodes?: string[];
  visibleQuestions?: string[];
  [key: string]: unknown;
}

export interface SessionEvent {
  eventType: SessionEventType;
  payload?: SessionEventPayload;
  clientTs: string;
  seq: number;
  deviceId?: string;
}

export interface BatchEventsRequest {
  surveyVersionId: string;
  events: SessionEvent[];
}

export interface BatchEventsResponse {
  success: boolean;
  acceptedCount: number;
  lastSeq: number;
  serverTs?: string;
  errors?: Array<{ seq: number; error: string }>;
}

export interface RandomizationMapData {
  questionOrder: string[];
  optionOrderByQuestionCode: Record<string, string[]>;
  matrixRowOrderByQuestionCode?: Record<string, string[]>;
  matrixColumnOrderByQuestionCode?: Record<string, string[]>;
  seed?: string;
}

export interface RandomizationMapRequest {
  surveyVersionId: string;
  blockId?: string;
  questionOrder: string[];
  optionOrderByQuestionCode: Record<string, string[]>;
  matrixRowOrderByQuestionCode?: Record<string, string[]>;
  matrixColumnOrderByQuestionCode?: Record<string, string[]>;
  seed?: string;
  hash: string;
}

export interface RandomizationMapResponse {
  success: boolean;
  id: string;
  isUpdate?: boolean;
}

// ============================================================================
// TELEMETRY SERVICE
// ============================================================================

const STORAGE_KEY = 'survey_telemetry_queue';
const BATCH_SIZE = 20;
const FLUSH_INTERVAL_MS = 5000; // 5 seconds
const MAX_RETRY_ATTEMPTS = 3;

/**
 * Telemetry service for collecting and sending session events
 */
class TelemetryService {
  private queue: SessionEvent[] = [];
  private seq = 0;
  private sessionId: string | null = null;
  private surveyVersionId: string | null = null;
  private deviceId: string | null = null;
  private flushTimer: ReturnType<typeof setTimeout> | null = null;
  private isFlushing = false;
  private retryCount = 0;

  /**
   * Initialize telemetry for a session
   */
  init(sessionId: string, surveyVersionId: string, deviceId?: string): void {
    this.sessionId = sessionId;
    this.surveyVersionId = surveyVersionId;
    this.deviceId = deviceId || this.getOrCreateDeviceId();
    this.seq = 0;
    this.queue = [];

    // Load any pending events from storage
    this.loadFromStorage();

    // Start flush timer
    this.startFlushTimer();

    // Emit session started event
    this.track(SessionEventType.SESSION_STARTED, {});
  }

  /**
   * Track an event
   */
  track(eventType: SessionEventType, payload?: SessionEventPayload): void {
    if (!this.sessionId) {
      console.warn('Telemetry not initialized, event dropped:', eventType);
      return;
    }

    const event: SessionEvent = {
      eventType,
      payload,
      clientTs: new Date().toISOString(),
      seq: this.seq++,
      deviceId: this.deviceId || undefined,
    };

    this.queue.push(event);
    this.saveToStorage();

    // Flush if we've hit batch size
    if (this.queue.length >= BATCH_SIZE) {
      this.flush();
    }
  }

  /**
   * Track page render
   */
  trackPageRendered(pageNumber: number, questionCodes: string[], visibleQuestions: string[]): void {
    this.track(SessionEventType.PAGE_RENDERED, {
      pageNumber,
      questionCodes,
      visibleQuestions,
    });
  }

  /**
   * Track answer change
   */
  trackAnswerChanged(
    questionCode: string,
    oldValue: unknown,
    newValue: unknown,
    valueType: string,
  ): void {
    this.track(SessionEventType.ANSWER_CHANGED, {
      questionCode,
      oldValue,
      newValue,
      valueType,
    });
  }

  /**
   * Track logic rule fired
   */
  trackLogicFired(
    ruleId: string,
    ruleType: string,
    conditionResult: boolean,
    action: string,
    targetQuestionCode?: string,
  ): void {
    this.track(SessionEventType.LOGIC_FIRED, {
      ruleId,
      ruleType,
      conditionResult,
      action,
      targetQuestionCode,
    });
  }

  /**
   * Track navigation
   */
  trackNavigation(fromQuestion: string, toQuestion: string, reason: string): void {
    this.track(SessionEventType.NAVIGATION, {
      fromQuestion,
      toQuestion,
      reason,
    });
  }

  /**
   * Track validation failure
   */
  trackValidationFailed(questionCode: string, validationType: string, message: string): void {
    this.track(SessionEventType.VALIDATION_FAILED, {
      questionCode,
      validationType,
      message,
    });
  }

  /**
   * Track session completion
   */
  trackSessionCompleted(): void {
    this.track(SessionEventType.SESSION_COMPLETED, {});
    // Flush immediately on completion
    this.flush();
  }

  /**
   * Track session termination
   */
  trackSessionTerminated(reason: string): void {
    this.track(SessionEventType.SESSION_TERMINATED, { reason });
    // Flush immediately on termination
    this.flush();
  }

  /**
   * Flush events to server
   */
  async flush(): Promise<void> {
    if (this.isFlushing || this.queue.length === 0 || !this.sessionId) {
      return;
    }

    this.isFlushing = true;

    try {
      const eventsToSend = this.queue.slice(0, BATCH_SIZE);

      const response = await apiClient.post<BatchEventsResponse>(
        `/api/v1/sessions/${this.sessionId}/events/batch`,
        {
          surveyVersionId: this.surveyVersionId,
          events: eventsToSend,
        } as BatchEventsRequest,
      );

      if (response.data.success) {
        // Remove sent events from queue
        this.queue = this.queue.slice(eventsToSend.length);
        this.saveToStorage();
        this.retryCount = 0;
      } else if (response.data.errors) {
        console.warn('Some events failed:', response.data.errors);
        // Still remove events to avoid infinite loop
        this.queue = this.queue.slice(eventsToSend.length);
        this.saveToStorage();
      }
    } catch (error) {
      console.error('Failed to flush telemetry:', error);
      this.retryCount++;

      // If we've exceeded retry attempts, save to storage and clear queue
      if (this.retryCount >= MAX_RETRY_ATTEMPTS) {
        console.warn('Max retry attempts reached, events saved to storage');
        this.saveToStorage();
      }
    } finally {
      this.isFlushing = false;
    }
  }

  /**
   * Save randomization map to server
   */
  async saveRandomizationMap(
    blockId: string | undefined,
    mapData: RandomizationMapData,
  ): Promise<RandomizationMapResponse | null> {
    if (!this.sessionId || !this.surveyVersionId) {
      console.warn('Telemetry not initialized, cannot save randomization map');
      return null;
    }

    try {
      const hash = this.generateMapHash(mapData);

      const response = await apiClient.post<RandomizationMapResponse>(
        `/api/v1/sessions/${this.sessionId}/randomization-map`,
        {
          surveyVersionId: this.surveyVersionId,
          blockId,
          questionOrder: mapData.questionOrder,
          optionOrderByQuestionCode: mapData.optionOrderByQuestionCode,
          matrixRowOrderByQuestionCode: mapData.matrixRowOrderByQuestionCode,
          matrixColumnOrderByQuestionCode: mapData.matrixColumnOrderByQuestionCode,
          seed: mapData.seed,
          hash,
        } as RandomizationMapRequest,
      );

      // Also track as event
      this.track(SessionEventType.RANDOMIZATION_APPLIED, {
        blockId,
        mapHash: hash,
        questionOrder: mapData.questionOrder,
      });

      return response.data;
    } catch (error) {
      console.error('Failed to save randomization map:', error);
      return null;
    }
  }

  /**
   * Stop telemetry and flush remaining events
   */
  async stop(): Promise<void> {
    this.stopFlushTimer();
    await this.flush();
    this.sessionId = null;
    this.surveyVersionId = null;
  }

  // ============================================================================
  // PRIVATE METHODS
  // ============================================================================

  private startFlushTimer(): void {
    this.stopFlushTimer();
    this.flushTimer = setInterval(() => {
      this.flush();
    }, FLUSH_INTERVAL_MS);
  }

  private stopFlushTimer(): void {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }
  }

  private saveToStorage(): void {
    try {
      const data = {
        sessionId: this.sessionId,
        surveyVersionId: this.surveyVersionId,
        seq: this.seq,
        queue: this.queue,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      // localStorage might be full or disabled
      console.warn('Failed to save telemetry to storage:', error);
    }
  }

  private loadFromStorage(): void {
    try {
      const data = localStorage.getItem(STORAGE_KEY);
      if (data) {
        const parsed = JSON.parse(data);
        // Only restore if same session
        if (parsed.sessionId === this.sessionId) {
          this.seq = parsed.seq || 0;
          this.queue = parsed.queue || [];
        } else {
          // Different session, clear old data
          localStorage.removeItem(STORAGE_KEY);
        }
      }
    } catch (error) {
      console.warn('Failed to load telemetry from storage:', error);
    }
  }

  private getOrCreateDeviceId(): string {
    const storageKey = 'survey_device_id';
    let deviceId = localStorage.getItem(storageKey);
    if (!deviceId) {
      deviceId = this.generateUUID();
      localStorage.setItem(storageKey, deviceId);
    }
    return deviceId;
  }

  private generateUUID(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  private generateMapHash(mapData: RandomizationMapData): string {
    // Simple hash for client-side (server will verify)
    const str = JSON.stringify({
      questionOrder: mapData.questionOrder,
      optionOrderByQuestionCode: mapData.optionOrderByQuestionCode,
    });
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash;
    }
    return Math.abs(hash).toString(16).padStart(16, '0').slice(0, 16);
  }
}

// Export singleton instance
export const telemetryService = new TelemetryService();

// Export class for testing
export { TelemetryService };
